import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { readGroupViewState } from "@/features/study-group-view-state/sessionStore";
import { deriveStudyGroupViewKey } from "@/features/study-group-view-state/viewKey";
import { streamReadApi } from "@/lib/humhub-api/streamRead";
import { topicsApi } from "@/lib/humhub-api/topics";
import { channelKeys } from "@/lib/queryKeys/channelKeys";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

/**
 * スタディグループのナビゲーションを処理するカスタムフック
 * スタディグループの情報を更新し、対応するストリームページに遷移します
 */
export const useStudyGroupNavigation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const userInfo = useGlobalUserInfo();

  const navigateToStudyGroup = useCallback(
    (studyGroupId: number) => {
      const activeGroupId = /^\/study-group\/(\d+)(?:\/|$)/.exec(pathname)?.[1];
      if (activeGroupId === String(studyGroupId)) return;

      void Promise.allSettled([
        queryClient.fetchQuery({
          queryKey: channelKeys.unreadCounts(studyGroupId),
          queryFn: () => topicsApi.getChannelUnreadCounts(studyGroupId),
        }),
        queryClient.fetchQuery({
          queryKey: ["stream-catch-up", studyGroupId, true],
          queryFn: () =>
            streamReadApi.getCatchUp(studyGroupId, {
              includeComments: true,
              limit: 100,
            }),
        }),
      ]);

      requestAnimationFrame(() => {
        try {
          const retainedState =
            userInfo?.id == null
              ? null
              : readGroupViewState(userInfo.id, studyGroupId);
          const retainedPathname = retainedState?.lastPathname;
          const destination =
            retainedPathname &&
            deriveStudyGroupViewKey(retainedPathname, studyGroupId) ===
              retainedState.lastViewKey
              ? retainedPathname
              : `/study-group/${studyGroupId}/stream`;
          const navigateToPath = navigate as unknown as (options: {
            to: string;
            replace: boolean;
          }) => void;
          navigateToPath({
            to: destination,
            replace: false,
          });
        } catch (error) {
          console.error("❌ Navigation error:", error);
        }
      });
    },
    [navigate, pathname, queryClient, userInfo?.id],
  );

  return { navigateToStudyGroup };
};
